import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    getMyAttendanceStatus,
    punchAttendance,
    getMyAttendancePunches,
    getAttendanceEnums,
} from '../../../redux/actions/SocietyPortal/AttendanceAction/AttendanceAction';
import { getCoords } from '../../../utils/helpers/geolocation';
import getBrowserDetails, { getDeviceType } from '../../../utils/helpers/getBrowserDetails';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_MY_ATTENDANCE } from '../../../Routing/routes';
import { usePageRefresh } from '../../../context/PageRefreshContext';
import SelfieCaptureModal from './Component/SelfieCaptureModal';
import WorkLogModal from './Component/WorkLogModal';
import IndeterminateProgress from '../../../components/IndeterminateProgress';
import { formatIstCustom, formatIstTime } from '../../../utils/helpers/istDate';

// One idempotency key per user tap: a network retry of the same tap replays
// the original punch server-side instead of creating a second one.
const newRequestId = () =>
    (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const getPlatform = () => {
    try {
        return JSON.parse(getBrowserDetails()).os || undefined;
    } catch {
        return undefined;
    }
};

const formatMinutes = (mins) => {
    const m = Math.max(0, Math.round(mins || 0));
    return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
};

// Live timer display: 3:24:07
const formatHMS = (totalSeconds) => {
    const s = Math.max(0, Math.floor(totalSeconds || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const formatTime = (dateStr) => {
    if (!dateStr) return '—';
    return formatIstTime(dateStr);
};

const WORK_LOG_STATUS_STYLES = {
    COMPLETED: 'tw-bg-emerald-50 tw-text-emerald-700',
    IN_PROGRESS: 'tw-bg-blue-50 tw-text-blue-700',
    PENDING: 'tw-bg-amber-50 tw-text-amber-700',
};

const DAY_STATUS_STYLES = {
    PRESENT: 'tw-bg-emerald-50 tw-text-emerald-700',
    HALF_DAY: 'tw-bg-amber-50 tw-text-amber-700',
    INCOMPLETE: 'tw-bg-orange-50 tw-text-orange-700',
    ABSENT: 'tw-bg-gray-100 tw-text-gray-500',
};

// A day that is still open is stored as INCOMPLETE until the punch out settles
// the worked minutes. Showing "Incomplete" to someone who has just punched in
// reads as a fault, so an open day says it is running instead (same wording as
// My Attendance).
const DayStatusBadge = ({ status, isOpen }) => {
    if (isOpen) {
        return (
            <span
                className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium tw-bg-sky-50 tw-text-sky-700"
                title="Punched in — the day is still open. It will be finalised when you punch out."
            >
                <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-sky-500 tw-animate-pulse" />
                In progress
            </span>
        );
    }
    return (
        <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium ${DAY_STATUS_STYLES[status] || 'tw-bg-gray-100 tw-text-gray-500'}`}>
            {(status || '—').replace(/_/g, ' ')}
        </span>
    );
};

// Punch location: resolved name → name; still geocoding → spinner text;
// SKIPPED (no provider / no coords) → raw coordinates. Never block on it.
const punchLocation = (p) => {
    if (p.location_name) return p.location_name;
    if (p.geocode_status === 'PENDING') return 'Resolving location…';
    if (p.latitude != null && p.longitude != null) {
        return `${Number(p.latitude).toFixed(5)}, ${Number(p.longitude).toFixed(5)}`;
    }
    return 'No location';
};

const PunchAttendance = () => {
    const dispatch = useDispatch();
    const {
        myStatusData: status,
        myStatusLoading,
        myStatusError,
        punchLoading,
        myPunches,
        myPunchesLoading,
        attendanceEnums,
    } = useSelector(state => state.AttendanceReducer || {});

    const [geofenceBlock, setGeofenceBlock] = useState(null);
    const [selfieFile, setSelfieFile] = useState(null);
    const [selfiePreview, setSelfiePreview] = useState(null);
    const [selfieError, setSelfieError] = useState(false);
    const [cameraOpen, setCameraOpen] = useState(false);
    // Work log draft survives a rejected punch (geofence, selfie, network) so
    // the employee never retypes it; cleared only once a punch is recorded.
    const [workLogOpen, setWorkLogOpen] = useState(false);
    const [workLogRows, setWorkLogRows] = useState([]);
    // Wall clock + live shift timer tick (1s while the screen is open)
    const [nowTs, setNowTs] = useState(() => Date.now());

    // The in-flight tap's idempotency key. Kept across network failures so a
    // retry replays the same punch; cleared once the server has decided.
    const requestIdRef = useRef(null);
    const statusFetchedAtRef = useRef(null);
    const selfieInputRef = useRef(null);

    const refreshStatus = useCallback(() => {
        getCoords().then(coords => {
            statusFetchedAtRef.current = Date.now();
            dispatch(getMyAttendanceStatus({
                latitude: coords.latitude,
                longitude: coords.longitude,
            }));
        });
    }, [dispatch]);

    const refreshTodayPunches = useCallback((attendanceDate) => {
        if (!attendanceDate) return;
        dispatch(getMyAttendancePunches({ from: attendanceDate, to: attendanceDate, limit: 20 }));
    }, [dispatch]);

    // Fetch on mount and whenever the tab regains focus, so a screen left open
    // overnight doesn't show yesterday's state.
    useEffect(() => {
        refreshStatus();
        const onVisible = () => {
            if (document.visibilityState === 'visible') refreshStatus();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [refreshStatus]);

    useEffect(() => {
        refreshTodayPunches(status?.attendance_date);
    }, [refreshTodayPunches, status?.attendance_date]);

    // Header refresh re-reads today's status and punches (never the punch itself).
    usePageRefresh(() => {
        refreshStatus();
        refreshTodayPunches(status?.attendance_date);
    });

    // work_log_statuses feeds the work log modal's status dropdown.
    useEffect(() => { dispatch(getAttendanceEnums()); }, [dispatch]);

    // 1-second tick drives the wall clock and, while punched in, the live
    // shift/worked-today timers.
    useEffect(() => {
        const id = setInterval(() => setNowTs(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    // Revoke the selfie preview object URL when it is replaced or on unmount
    useEffect(() => {
        return () => { if (selfiePreview) URL.revokeObjectURL(selfiePreview); };
    }, [selfiePreview]);

    const isIn = status?.current_state === 'IN';
    const today = status?.today || {};

    // Anchor the live timer to the open IN punch's server timestamp when we
    // have it (accurate to the second); fall back to open_shift_minutes as of
    // the status fetch.
    const openShiftStartMs = useMemo(() => {
        if (!isIn) return null;
        const newest = (myPunches || [])[0];
        if (newest?.punch_type === 'IN' && newest?.punch_at) {
            return new Date(newest.punch_at).getTime();
        }
        const fetchedAt = statusFetchedAtRef.current || Date.now();
        return fetchedAt - (today.open_shift_minutes || 0) * 60000;
    }, [isIn, myPunches, today.open_shift_minutes]);

    const liveShiftSeconds = isIn && openShiftStartMs != null
        ? Math.max(0, Math.floor((nowTs - openShiftStartMs) / 1000))
        : 0;
    // total_worked_minutes covers CLOSED in→out pairs only — the open shift
    // accrues separately, so live worked-today is their sum.
    const liveWorkedSeconds = (today.total_worked_minutes || 0) * 60 + liveShiftSeconds;

    const clearSelfie = () => {
        setSelfieFile(null);
        setSelfiePreview(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
        if (selfieInputRef.current) selfieInputRef.current.value = '';
    };

    // Shared by both paths: webcam capture (confirmed in the modal) and file upload
    const setSelfie = (file) => {
        if (file && file.size > 5 * 1024 * 1024) {
            toast.error('Photo must be 5MB or smaller');
            return;
        }
        setSelfieFile(file);
        setSelfiePreview(prev => {
            if (prev) URL.revokeObjectURL(prev);
            return file ? URL.createObjectURL(file) : null;
        });
        setSelfieError(false);
    };

    const handleSelfieChange = (e) => {
        setSelfie(e.target.files?.[0] || null);
        e.target.value = '';
    };

    // `workLogs` is only ever set on an OUT punch — the BE rejects rows on an
    // IN punch (400 WORK_LOG_NOT_ALLOWED).
    const submitPunch = (workLogs) => {
        if (!requestIdRef.current) requestIdRef.current = newRequestId();
        setGeofenceBlock(null);

        getCoords().then(coords => {
            dispatch(punchAttendance({
                latitude: coords.latitude,
                longitude: coords.longitude,
                accuracy_meters: coords.accuracy_meters,
                client_request_id: requestIdRef.current,
                client_punch_at: new Date().toISOString(),
                source: 'WEB',
                device_type: getDeviceType(),
                platform: getPlatform(),
                selfie: selfieFile || undefined,
                work_logs: workLogs || undefined,
                onSuccess: (result) => {
                    requestIdRef.current = null;
                    clearSelfie();
                    setWorkLogOpen(false);
                    setWorkLogRows([]);
                    if (result?.punch?.status === 'PENDING_APPROVAL') {
                        toast.info('You were outside the premises — this punch is pending admin approval, but your hours are counting.');
                    }
                    refreshStatus();
                },
                onError: (info) => {
                    // Only an undecided request (no server response) keeps its
                    // idempotency key for a retry; every decided outcome ends the tap.
                    if (info.code || info.data) requestIdRef.current = null;

                    if (info.code === 'ALREADY_PUNCHED_IN' || info.code === 'NOT_PUNCHED_IN') {
                        // Stale UI — the response carries the real state; re-sync quietly.
                        setWorkLogOpen(false);
                        toast.info('Your attendance status had changed — screen refreshed.');
                        refreshStatus();
                    } else if (info.code === 'GEOFENCE_BLOCKED') {
                        // Close the modal so the banner behind it is readable;
                        // the typed rows are kept for the next attempt.
                        setWorkLogOpen(false);
                        setGeofenceBlock(info.data?.geofence || {});
                        refreshStatus();
                    } else if (info.code === 'SELFIE_REQUIRED') {
                        setWorkLogOpen(false);
                        setSelfieError(true);
                        toast.warn('A photo is required to punch — please add one first.');
                    } else if (info.code === 'WORK_LOG_REQUIRED') {
                        // The society turned the requirement on after this screen
                        // loaded — re-prompt with the form instead of failing.
                        setWorkLogOpen(true);
                        toast.warn(info.message || 'Add at least one work log entry before punching out.');
                    } else if (info.code === 'TODO_REQUIRED' || info.code === 'TODO_NOT_FOUND') {
                        // The to-do list changed after this screen loaded:
                        // reload it and ask again, keeping what was typed.
                        refreshStatus();
                        setWorkLogOpen(true);
                        toast.warn(info.message || 'Update your to-do list before punching out.');
                    } else if (info.code === 'WORK_LOG_NOT_ALLOWED') {
                        // Rows on an IN punch — our state was stale; drop them.
                        setWorkLogOpen(false);
                        setWorkLogRows([]);
                        toast.info('Your attendance status had changed — screen refreshed.');
                        refreshStatus();
                    } else {
                        setWorkLogOpen(false);
                    }
                },
            }));
        });
    };

    const handlePunch = () => {
        if (punchLoading || !status?.can_punch) return;

        const selfieMode = status?.society?.selfie_mode || 'OFF';
        if (selfieMode === 'REQUIRED' && !selfieFile) {
            setSelfieError(true);
            toast.warn('A photo is required to punch — please add one first.');
            return;
        }

        // Punching out of a society that requires a work log, or with daily
        // to-dos still to mark: collect the rows first, then punch from the modal.
        if (status?.next_punch_type === 'OUT' && (status?.society?.work_log_enabled === true || pendingTodos.length > 0)) {
            setWorkLogOpen(true);
            return;
        }

        submitPunch(null);
    };

    // Today's daily to-do items, and those not yet answered on an earlier punch-out.
    const dailyTodos = Array.isArray(status?.daily_todos) ? status.daily_todos : [];
    const pendingTodos = dailyTodos.filter(t => !t.answered);

    const handleWorkLogSubmit = (rows) => {
        setWorkLogRows(rows);
        submitPunch(rows);
    };

    // ---- blocked / empty states -------------------------------------------
    const blockedCode = myStatusError?.code;
    if (!status && blockedCode === 'NOT_AN_EMPLOYEE') {
        return (
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-10 tw-text-center">
                <Icon icon="mdi:account-off-outline" className="tw-text-5xl tw-text-gray-200 tw-mx-auto" />
                <h2 className="tw-text-base tw-font-semibold tw-text-gray-700 tw-mt-3">Attendance isn't available for your account</h2>
                <p className="tw-text-xs tw-text-gray-400 tw-mt-1">Punch in/out is only for registered employees of this society. If you believe this is a mistake, ask your society admin to link your employee record.</p>
            </div>
        );
    }
    if (!status && blockedCode === 'ATTENDANCE_DISABLED') {
        return (
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-10 tw-text-center">
                <Icon icon="mdi:clock-remove-outline" className="tw-text-5xl tw-text-gray-200 tw-mx-auto" />
                <h2 className="tw-text-base tw-font-semibold tw-text-gray-700 tw-mt-3">Attendance is not enabled for this society</h2>
                <p className="tw-text-xs tw-text-gray-400 tw-mt-1">Ask your society admin to enable the attendance module.</p>
            </div>
        );
    }
    if (!status && myStatusLoading) {
        return (
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-16 tw-text-center">
                <Icon icon="mdi:loading" className="tw-animate-spin tw-text-3xl tw-text-gray-300 tw-mx-auto" />
                <p className="tw-text-xs tw-text-gray-400 tw-mt-2">Checking your attendance status…</p>
            </div>
        );
    }
    if (!status) {
        return (
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-10 tw-text-center">
                <Icon icon="mdi:alert-circle-outline" className="tw-text-5xl tw-text-gray-200 tw-mx-auto" />
                <h2 className="tw-text-base tw-font-semibold tw-text-gray-700 tw-mt-3">Couldn't load your attendance status</h2>
                {myStatusError?.message && <p className="tw-text-xs tw-text-gray-400 tw-mt-1">{myStatusError.message}</p>}
                <button
                    onClick={refreshStatus}
                    className="tw-mt-4 tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 tw-rounded-md hover:tw-bg-blue-700"
                >
                    <Icon icon="mdi:refresh" /> Try again
                </button>
            </div>
        );
    }

    // ---- derived display state --------------------------------------------
    const geo = status.geofence || {};
    const selfieMode = status.society?.selfie_mode || 'OFF';
    const punchOut = status.next_punch_type === 'OUT';
    // Note the type flip: the society endpoints expose this as the integer
    // attendance_work_log_enabled, /me/status as the boolean work_log_enabled.
    const workLogRequired = punchOut && status.society?.work_log_enabled === true;

    const blockedMessage = (() => {
        if (status.can_punch) return null;
        switch (status.blocked_reason) {
            case 'OUTSIDE_GEOFENCE':
                return geo.distance_meters != null && geo.radius_meters != null
                    ? `You are ${Math.round(geo.distance_meters)}m from the premises — move within ${Math.round(geo.radius_meters)}m to punch.`
                    : 'You are outside the society premises. Move closer to punch.';
            case 'LOCATION_REQUIRED':
                return 'Location access is required to punch. Allow location for this site, then retry.';
            case 'ATTENDANCE_DISABLED':
                return 'Attendance is not enabled for this society.';
            default:
                return 'Punching is currently unavailable.';
        }
    })();

    const dateLabel = status.attendance_date
        ? formatIstCustom(status.attendance_date, {
            weekday: 'long', day: '2-digit', month: 'short', year: 'numeric',
        })
        : '';

    const wallClock = formatIstCustom(nowTs, {
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    });

    const todayTiles = [
        { icon: 'mdi:login', color: 'tw-bg-emerald-50 tw-text-emerald-600', label: 'First in', value: formatTime(today.first_in_at) },
        { icon: 'mdi:logout', color: 'tw-bg-rose-50 tw-text-rose-600', label: 'Last out', value: formatTime(today.last_out_at) },
        {
            icon: 'mdi:timer-outline', color: 'tw-bg-blue-50 tw-text-blue-600', label: 'Worked today',
            value: isIn ? formatHMS(liveWorkedSeconds) : (today.total_worked_display || formatMinutes(today.total_worked_minutes)),
            live: isIn,
        },
        { icon: 'mdi:gesture-tap', color: 'tw-bg-purple-50 tw-text-purple-600', label: 'Punches', value: today.punch_count || 0 },
    ];

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-start tw-justify-between tw-mb-4 tw-gap-3 tw-flex-wrap">
                <div>
                    <h1 className="tw-text-lg tw-font-bold tw-text-gray-800">Attendance</h1>
                    <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">
                        {status.employee?.name}
                        {status.employee?.employee_code ? ` (${status.employee.employee_code})` : ''}
                        {status.society?.name ? ` · ${status.society.name}` : ''}
                    </p>
                </div>
                <Link
                    to={SOCIETY_PORTAL + SOCIETY_PORTAL_MY_ATTENDANCE}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-blue-700 tw-border tw-border-blue-200 tw-rounded-md hover:tw-bg-blue-50"
                >
                    <Icon icon="mdi:calendar-month-outline" /> My attendance history
                </Link>
            </div>

            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-5 tw-gap-4">
                {/* Punch card */}
                <div className="lg:tw-col-span-3 tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-p-6 tw-flex tw-flex-col tw-items-center">
                    {/* Wall clock */}
                    <p className="tw-text-[28px] tw-font-bold tw-text-gray-800 tw-tabular-nums tw-tracking-tight tw-m-0 tw-leading-none">{wallClock}</p>
                    <p className="tw-text-xs tw-text-gray-400 tw-mt-1.5 tw-mb-0">{dateLabel}</p>

                    {/* Live shift timer */}
                    <div className={`tw-mt-4 tw-w-full tw-max-w-[320px] tw-rounded-xl tw-py-3 tw-px-4 tw-text-center ${isIn ? 'tw-bg-emerald-50' : 'tw-bg-gray-50'}`}>
                        {isIn ? (
                            <>
                                <div className="tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-text-[11px] tw-font-medium tw-text-emerald-600 tw-uppercase tw-tracking-wider">
                                    <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-emerald-500 tw-animate-pulse" />
                                    On shift
                                </div>
                                <div className="tw-text-[32px] tw-leading-tight tw-font-bold tw-text-emerald-700 tw-tabular-nums">
                                    {formatHMS(liveShiftSeconds)}
                                </div>
                                <div className="tw-text-[11px] tw-text-emerald-600/70">
                                    since {formatTime(new Date(openShiftStartMs).toISOString())}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="tw-text-[11px] tw-font-medium tw-text-gray-400 tw-uppercase tw-tracking-wider">Not punched in</div>
                                <div className="tw-text-[32px] tw-leading-tight tw-font-bold tw-text-gray-300 tw-tabular-nums">0:00:00</div>
                                {(today.total_worked_minutes || 0) > 0 && (
                                    <div className="tw-text-[11px] tw-text-gray-400">
                                        worked {today.total_worked_display || formatMinutes(today.total_worked_minutes)} today
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={handlePunch}
                        disabled={!status.can_punch || punchLoading || myStatusLoading}
                        className={`tw-mt-6 tw-w-44 tw-h-44 tw-rounded-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1.5 tw-text-white tw-font-semibold tw-shadow-lg tw-transition-all active:tw-scale-95 hover:tw-shadow-xl disabled:tw-opacity-40 disabled:tw-cursor-not-allowed tw-ring-8 ${punchOut
                            ? 'tw-bg-rose-500 hover:tw-bg-rose-600 tw-ring-rose-100'
                            : 'tw-bg-emerald-500 hover:tw-bg-emerald-600 tw-ring-emerald-100'}`}
                    >
                        {punchLoading ? (
                            <Icon icon="mdi:loading" className="tw-animate-spin tw-text-4xl" />
                        ) : (
                            <>
                                <Icon icon={punchOut ? 'mdi:logout' : 'mdi:login'} className="tw-text-4xl" />
                                <span className="tw-text-base">Punch {punchOut ? 'Out' : 'In'}</span>
                                <span className="tw-text-[10px] tw-font-normal tw-opacity-80">
                                    {punchOut
                                        ? (workLogRequired ? 'Log your work & end shift' : 'End your shift')
                                        : 'Start your shift'}
                                </span>
                            </>
                        )}
                    </button>

                    {/* Punch in flight — the button is disabled, so show progress
                        under it rather than leaving the screen looking idle. */}
                    <IndeterminateProgress
                        active={punchLoading}
                        color={punchOut ? 'tw-bg-rose-500' : 'tw-bg-emerald-500'}
                        label={`Recording your punch ${punchOut ? 'out' : 'in'}…`}
                        className="tw-mt-5 tw-w-full tw-max-w-[320px]"
                    />

                    {/* Geofence status line */}
                    {geo.enabled && geo.configured && (
                        <p className={`tw-mt-5 tw-mb-0 tw-text-xs tw-flex tw-items-center tw-gap-1 ${geo.within ? 'tw-text-emerald-600' : 'tw-text-amber-600'}`}>
                            <Icon icon={geo.within ? 'mdi:map-marker-check-outline' : 'mdi:map-marker-distance'} />
                            {geo.within
                                ? `Inside premises${geo.distance_meters != null ? ` (${Math.round(geo.distance_meters)}m from center)` : ''}`
                                : geo.location_provided
                                    ? `${geo.distance_meters != null ? `${Math.round(geo.distance_meters)}m away` : 'Outside premises'}${geo.radius_meters != null ? ` — allowed within ${Math.round(geo.radius_meters)}m` : ''}`
                                    : 'Location not available'}
                        </p>
                    )}

                    {/* Blocked explanation */}
                    {blockedMessage && (
                        <div className="tw-mt-3 tw-w-full tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-md tw-p-3 tw-text-xs tw-text-amber-800 tw-flex tw-items-start tw-gap-2">
                            <Icon icon="mdi:alert-outline" className="tw-text-base tw-flex-shrink-0 tw-mt-0.5" />
                            <div>
                                <span>{blockedMessage}</span>
                                {status.blocked_reason === 'LOCATION_REQUIRED' && (
                                    <button onClick={refreshStatus} className="tw-block tw-mt-1.5 tw-font-medium tw-text-amber-900 tw-underline">
                                        Retry with location
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Punch rejected by an enforced geofence */}
                    {geofenceBlock && (
                        <div className="tw-mt-3 tw-w-full tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-md tw-p-3 tw-text-xs tw-text-red-700 tw-flex tw-items-start tw-gap-2">
                            <Icon icon="mdi:map-marker-remove-outline" className="tw-text-base tw-flex-shrink-0 tw-mt-0.5" />
                            <span>
                                Punch blocked — you're outside the society premises
                                {geofenceBlock.distance_meters != null ? ` (${Math.round(geofenceBlock.distance_meters)}m away` : ''}
                                {geofenceBlock.distance_meters != null && geofenceBlock.radius_meters != null ? `, allowed within ${Math.round(geofenceBlock.radius_meters)}m)` : geofenceBlock.distance_meters != null ? ')' : ''}
                                . Move closer and try again.
                            </span>
                        </div>
                    )}

                    {/* Work log notice — the modal opens on the Punch Out tap */}
                    {workLogRequired && (
                        <div className="tw-mt-4 tw-w-full tw-rounded-xl tw-border tw-border-gray-200 tw-bg-gray-50/50 tw-p-3 tw-flex tw-items-start tw-gap-2.5">
                            <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-blue-50 tw-text-blue-600 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                <Icon icon="mdi:clipboard-text-clock-outline" className="tw-text-base" />
                            </div>
                            <div className="tw-min-w-0">
                                <p className="tw-text-xs tw-font-medium tw-text-gray-700 tw-m-0">
                                    Work log <span className="tw-text-red-500">(required to punch out)</span>
                                </p>
                                <p className="tw-text-[10px] tw-text-gray-400 tw-mt-0.5 tw-mb-0">
                                    {workLogRows.length > 0
                                        ? `${workLogRows.length} ${workLogRows.length === 1 ? 'entry' : 'entries'} saved from your last attempt — tap Punch Out to review and submit.`
                                        : 'Tap Punch Out and describe what you worked on this shift.'}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Selfie capture — only when the society asks for it */}
                    {selfieMode !== 'OFF' && (
                        <div className={`tw-mt-5 tw-w-full tw-rounded-xl tw-border tw-p-3 ${selfieError ? 'tw-border-red-300 tw-bg-red-50' : 'tw-border-gray-200 tw-bg-gray-50/50'}`}>
                            <div className="tw-flex tw-items-center tw-gap-3">
                                {selfiePreview ? (
                                    <div className="tw-relative tw-flex-shrink-0">
                                        <img
                                            src={selfiePreview}
                                            alt="Selfie preview"
                                            className="tw-w-14 tw-h-14 tw-rounded-lg tw-object-cover tw-border tw-border-gray-200"
                                        />
                                        <button
                                            type="button"
                                            onClick={clearSelfie}
                                            className="tw-absolute -tw-top-1.5 -tw-right-1.5 tw-w-5 tw-h-5 tw-rounded-full tw-bg-gray-700 tw-text-white tw-flex tw-items-center tw-justify-center hover:tw-bg-red-500"
                                            title="Remove photo"
                                        >
                                            <Icon icon="mdi:close" className="tw-text-xs" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className={`tw-w-14 tw-h-14 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${selfieError ? 'tw-bg-red-100 tw-text-red-400' : 'tw-bg-gray-100 tw-text-gray-400'}`}>
                                        <Icon icon="mdi:camera-outline" className="tw-text-2xl" />
                                    </div>
                                )}
                                <div className="tw-flex-1 tw-min-w-0">
                                    <p className="tw-text-xs tw-font-medium tw-text-gray-700 tw-m-0">
                                        Photo {selfieMode === 'REQUIRED'
                                            ? <span className="tw-text-red-500">(required to punch)</span>
                                            : <span className="tw-text-gray-400">(optional)</span>}
                                    </p>
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-mt-0.5 tw-mb-0">JPG, PNG, WebP or HEIC · max 5MB</p>
                                </div>
                                <div className="tw-flex-shrink-0 tw-flex tw-gap-1.5">
                                    <button
                                        type="button"
                                        onClick={() => setCameraOpen(true)}
                                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 tw-rounded-md hover:tw-bg-blue-700"
                                    >
                                        <Icon icon="mdi:camera" />
                                        {selfieFile ? 'Retake' : 'Take photo'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => selfieInputRef.current?.click()}
                                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-blue-700 tw-border tw-border-blue-200 tw-rounded-md tw-bg-white hover:tw-bg-blue-50"
                                        title="Choose an existing photo"
                                    >
                                        <Icon icon="mdi:upload-outline" />
                                        Upload
                                    </button>
                                </div>
                            </div>
                            {/* No `capture` attr: on mobile it would force the camera
                                and block gallery picking — the camera has its own modal. */}
                            <input
                                ref={selfieInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/heic"
                                onChange={handleSelfieChange}
                                className="tw-hidden"
                            />
                            {selfieFile && (
                                <p className="tw-text-[11px] tw-text-emerald-600 tw-mt-2 tw-mb-0 tw-flex tw-items-center tw-gap-1">
                                    <Icon icon="mdi:check-circle-outline" />
                                    Photo attached — it will be uploaded with your Punch {punchOut ? 'Out' : 'In'}.
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* Today card */}
                <div className="lg:tw-col-span-2 tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-p-5">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                        <h2 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-m-0">Today</h2>
                        <DayStatusBadge status={today.day_status} isOpen={isIn} />
                    </div>

                    <div className="tw-grid tw-grid-cols-2 tw-gap-2.5 tw-mb-5">
                        {todayTiles.map(tile => (
                            <div key={tile.label} className="tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-flex tw-items-center tw-gap-2.5">
                                <div className={`tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${tile.color}`}>
                                    <Icon icon={tile.icon} className="tw-text-base" />
                                </div>
                                <div className="tw-min-w-0">
                                    <div className={`tw-text-sm tw-font-semibold tw-text-gray-800 tw-truncate ${tile.live ? 'tw-tabular-nums' : ''}`}>{tile.value}</div>
                                    <div className="tw-text-[10px] tw-text-gray-400">{tile.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {dailyTodos.length > 0 && (
                        <div className="tw-mb-4">
                            <h3 className="tw-text-[11px] tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-2">
                                Today's to-do list · {dailyTodos.length - pendingTodos.length} / {dailyTodos.length} marked
                            </h3>
                            <ul className="tw-m-0 tw-p-0 tw-list-none tw-border tw-border-gray-100 tw-rounded-lg tw-divide-y tw-divide-gray-100">
                                {dailyTodos.map(t => (
                                    <li key={t.todo_id} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-3 tw-py-2">
                                        <div className="tw-min-w-0">
                                            <p className="tw-text-xs tw-font-medium tw-text-gray-700 tw-m-0 tw-truncate">{t.title}</p>
                                            {t.description && <p className="tw-text-[10px] tw-text-gray-400 tw-m-0 tw-truncate">{t.description}</p>}
                                        </div>
                                        <span className={`tw-text-[10px] tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-full tw-whitespace-nowrap ${
                                            !t.answered ? 'tw-bg-gray-100 tw-text-gray-500'
                                                : t.status === 'COMPLETED' ? 'tw-bg-emerald-50 tw-text-emerald-700'
                                                    : t.status === 'IN_PROGRESS' ? 'tw-bg-blue-50 tw-text-blue-700' : 'tw-bg-amber-50 tw-text-amber-700'}`}>
                                            {!t.answered ? 'Mark at punch-out' : t.status === 'COMPLETED' ? 'Done' : t.status === 'IN_PROGRESS' ? 'In progress' : 'Not done'}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <h3 className="tw-text-[11px] tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-2">Today's punches</h3>
                    {myPunchesLoading ? (
                        <div className="tw-py-6 tw-text-center">
                            <Icon icon="mdi:loading" className="tw-animate-spin tw-text-xl tw-text-gray-300 tw-mx-auto" />
                        </div>
                    ) : (myPunches || []).length === 0 ? (
                        <div className="tw-py-8 tw-text-center">
                            <Icon icon="mdi:gesture-tap" className="tw-text-3xl tw-text-gray-200 tw-mx-auto" />
                            <p className="tw-text-xs tw-text-gray-400 tw-mt-2 tw-mb-0">No punches yet today.</p>
                        </div>
                    ) : (
                        /* Timeline — newest first, dot + connecting line */
                        <ul className="tw-m-0 tw-p-0 tw-list-none tw-relative">
                            {myPunches.map((p, idx) => (
                                <li key={p.punch_id} className="tw-relative tw-pl-9 tw-pb-4 last:tw-pb-0">
                                    {idx < myPunches.length - 1 && (
                                        <span className="tw-absolute tw-left-[13px] tw-top-7 tw-bottom-0 tw-w-px tw-bg-gray-200" aria-hidden="true" />
                                    )}
                                    <span className={`tw-absolute tw-left-0 tw-top-0 tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center ${p.punch_type === 'IN' ? 'tw-bg-emerald-50 tw-text-emerald-600' : 'tw-bg-rose-50 tw-text-rose-600'}`}>
                                        <Icon icon={p.punch_type === 'IN' ? 'mdi:login' : 'mdi:logout'} className="tw-text-sm" />
                                    </span>
                                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                                        <div className="tw-min-w-0">
                                            <div className="tw-text-xs tw-font-medium tw-text-gray-800">
                                                {p.punch_type === 'IN' ? 'Punched in' : 'Punched out'}
                                                <span className="tw-text-gray-400 tw-font-normal"> · {formatTime(p.punch_at)}</span>
                                            </div>
                                            <div className="tw-text-[10px] tw-text-gray-400 tw-truncate">{punchLocation(p)}</div>
                                        </div>
                                        <div className="tw-flex tw-gap-1 tw-flex-shrink-0">
                                            {p.is_within_geofence === false && (
                                                <span className="tw-text-[10px] tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-amber-50 tw-text-amber-700 tw-whitespace-nowrap">Outside</span>
                                            )}
                                            {p.status === 'PENDING_APPROVAL' && (
                                                <span className="tw-text-[10px] tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-blue-50 tw-text-blue-700 tw-whitespace-nowrap" title="An admin needs to confirm this punch. Your hours still count unless it is rejected.">
                                                    Pending approval
                                                </span>
                                            )}
                                            {p.status === 'REJECTED' && (
                                                <span className="tw-text-[10px] tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-red-50 tw-text-red-700 tw-whitespace-nowrap">Rejected</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Work log rows hang off the OUT punch they were submitted with */}
                                    {(p.work_logs || []).length > 0 && (
                                        <ul className="tw-mt-1.5 tw-mb-0 tw-pl-0 tw-list-none tw-flex tw-flex-col tw-gap-1">
                                            {p.work_logs.map(w => (
                                                <li key={w.work_log_id} className="tw-bg-gray-50 tw-rounded tw-px-2 tw-py-1.5">
                                                    <div className="tw-flex tw-items-center tw-gap-1.5">
                                                        <span className="tw-text-[10px] tw-font-medium tw-text-gray-700 tw-truncate">{w.work_type}</span>
                                                        <span className={`tw-text-[9px] tw-px-1.5 tw-py-px tw-rounded tw-whitespace-nowrap ${WORK_LOG_STATUS_STYLES[w.status] || 'tw-bg-gray-100 tw-text-gray-500'}`}>
                                                            {(w.status || '').replace(/_/g, ' ').toLowerCase()}
                                                        </span>
                                                    </div>
                                                    <p className="tw-text-[10px] tw-text-gray-400 tw-m-0 tw-line-clamp-2">{w.description}</p>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <SelfieCaptureModal
                isOpen={cameraOpen}
                onClose={() => setCameraOpen(false)}
                onCapture={setSelfie}
            />

            <WorkLogModal
                isOpen={workLogOpen}
                onClose={() => setWorkLogOpen(false)}
                onSubmit={handleWorkLogSubmit}
                busy={punchLoading}
                initialRows={workLogRows}
                statuses={attendanceEnums?.work_log_statuses}
                todos={pendingTodos}
            />
        </>
    );
};

export default PunchAttendance;
